import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeName, themes } from '@config/theme';

interface ThemeContextValue {
  currentTheme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  availableThemes: ThemeName[];
  themeConfig: typeof themes[ThemeName];
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = 'ai-buddy-theme';

// Default theme
const DEFAULT_THEME: ThemeName = 'dark';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentThemeState] = useState<ThemeName>(() => {
    // Load theme from localStorage on initialization
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    return (savedTheme as ThemeName) || DEFAULT_THEME;
  });

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, currentTheme);
  }, [currentTheme]);

  const setTheme = (theme: ThemeName) => {
    if (themes[theme]) {
      setCurrentThemeState(theme);
    }
  };

  const availableThemes: ThemeName[] = Object.keys(themes) as ThemeName[];

  const value: ThemeContextValue = {
    currentTheme,
    setTheme,
    availableThemes,
    themeConfig: themes[currentTheme],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
